import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import pl from "./locales/pl.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";

const LANGUAGE_STORAGE_KEY = "vp-language";

// Spread each top-level key as its own namespace so that both
//   useTranslation()                → t('sidebar.x')        (translation ns)
//   useTranslation('animatedWelcomeChat') → t('greeting.x') (animatedWelcomeChat ns)
// work correctly from the same flat JSON files.
const resources = {
  pl: { translation: pl, ...pl },
  en: { translation: en, ...en },
  es: { translation: es, ...es },
  pt: { translation: pt, ...pt },
};

export type SupportedLanguage = "pl" | "en" | "es" | "pt";

export const SUPPORTED_LANGUAGES: ReadonlyArray<{
  code: SupportedLanguage;
  label: string;
  flag: string;
}> = [
  { code: "pl", label: "Polski", flag: "🇵🇱" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pt", label: "Português (Brasil)", flag: "🇧🇷" },
] as const;

export const ENABLED_UI_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  ({ code }) => code === "pl" || code === "en"
);

export function getBaseLanguage(language?: string | null): SupportedLanguage {
  const base = language?.split("-")[0];
  return SUPPORTED_LANGUAGES.some(({ code }) => code === base) ? (base as SupportedLanguage) : "pl";
}

export async function setAppLanguage(language: SupportedLanguage): Promise<void> {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }
  await i18n.changeLanguage(language);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "pl",
    load: "languageOnly",
    interpolation: {
      escapeValue: false,
    },
    // VP-206: rozjazd nazwy placeholdera w locale i zmiennej w t() renderuje
    // użytkownikowi surowe "{{...}}" — i18next domyślnie przemilcza taki brak.
    // W dev logujemy każdy przypadek, żeby ta klasa buga nie wracała. Zwracamy
    // placeholder, bo zwrot nie-stringa i18next podmienia na pusty string,
    // a to zmieniałoby dotychczasowe zachowanie renderowania.
    missingInterpolationHandler: (text: string, value: unknown): string => {
      const placeholder = Array.isArray(value) ? String(value[0]) : String(value);
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Brak zmiennej interpolacji ${placeholder} w stringu: "${text}"`);
      }
      return placeholder;
    },
    detection: {
      // Only honor explicit user choice (localStorage). Browser navigator language
      // is intentionally NOT consulted — most users have English-locale OSes
      // but expect the app/AI in Polish (the product's primary market).
      // After login, useAuth syncs i18n.language with user.locale from DB.
      order: ["localStorage"],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
    },
  });

export default i18n;
